import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLHVQDiscrepanciesUtil } from 'ml-portals-util-js';
import { MLModalService } from 'ml-portals-services-react';
import { MLHVQStepsUtil } from 'ml-services-common-react';

import MLHVQDiscrepancyPropertyGroupComponent from '../../components/MLHVQDiscrepancyPropertyGroupComponent/MLHVQDiscrepancyPropertyGroupComponent';

import messages from './MLPAHVQConsumerDiscrepanciesPage.messages';
import metadata from './MLPAHVQConsumerDiscrepanciesPage.metadata.json5';

function MLPAHVQConsumerDiscrepanciesPage(props) {
    const {
        wizardData: submissionVM,
        goNext,
        updateWizardData,
        steps,
        currentStep,
        changeNextSteps,
        isSkipping
    } = props;
    const translator = useTranslator();
    const [discrepancyVMs] = useState([]);
    const [discrepancyCategories, setDiscrepancyCategories] = useState([]);

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const {
        getLOBPendingDiscrepancyItemVMs,
        getPropertyVMsFromVMItemWithDefaultSelection,
        isVehicleItemVM,
        isDriverItemVM,
        isPriorCarrierItemVM,
        markAllDiscrepancyItemsCompleted 
    } = MLHVQDiscrepanciesUtil();

    const { processPaHVQConsumerAdditionalDriversCheck } = MLHVQStepsUtil;

    const skipOnResumeIf = () => {
        if (!!_.get(submissionVM, 'bindData.policyNumber.value')
            && (submissionVM.value.baseData.status_ML === 'Bound' || submissionVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }
        return !(submissionVM.hvqQuoteID_ML && submissionVM.hvqQuoteID_ML.value);
    };

    useEffect(() => {
        if (!isSkipping || (isSkipping && !skipOnResumeIf())) {
            const pendingDiscrepancyitemVMs = getLOBPendingDiscrepancyItemVMs(submissionVM);
            pendingDiscrepancyitemVMs.forEach((itemVM) => {
                discrepancyVMs.push({
                    itemVM,
                    propertyVMs: getPropertyVMsFromVMItemWithDefaultSelection(itemVM)
                });
            });

            setDiscrepancyCategories({
                vehicles: discrepancyVMs.filter((discrepacyVM) => isVehicleItemVM(discrepacyVM.itemVM)),
                drivers: discrepancyVMs.filter((discrepacyVM) => isDriverItemVM(discrepacyVM.itemVM)),
                priorCarriers:discrepancyVMs.filter((discrepacyVM) => isPriorCarrierItemVM(discrepacyVM.itemVM))
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const generateOverrides = () => {
        const vehicleOverrides = _.get(discrepancyCategories, 'vehicles', []).map((dicrepancyVM, index) => {
            return {
                [`summaryVehicleItem${index}`]: {
                    content: translator(messages.vehicleX, { index: index + 1})
                },
                [`summaryHeaderItemTitle${index}`]: {
                    content: dicrepancyVM.itemVM.title.value
                },
                [`vehicleDiscrepancyPropertyGroup${index}`]: {
                    discrepancyProperties: dicrepancyVM.propertyVMs
                }
            };
        });

        const driverOverrides = _.get(discrepancyCategories, 'drivers', []).map((dicrepancyVM, index) => {
            return {
                [`driverX${index}`]: {
                    content: translator(messages.driverX, { index: index + 1})
                },
                [`driverItemTitle${index}`]: {
                    content: dicrepancyVM.itemVM.title.value
                },
                [`driverDiscrepancyPropertyGroup${index}`]: {
                    discrepancyProperties: dicrepancyVM.propertyVMs
                }
            };
        });

        const priorInsuranceOverrides = _.get(discrepancyCategories, 'priorCarriers', []).map((dicrepancyVM, index) => {
            return {
                [`priorInsuranceDiscrepancyPropertyGroup${index}`]: {
                    discrepancyProperties: dicrepancyVM.propertyVMs
                }
            };
        });

        return Object.assign({}, ...vehicleOverrides, ...driverOverrides, priorInsuranceOverrides);
    };

    const overrideProps = {
        vehiclesDiscrepancy: {
            data: _.get(discrepancyCategories, 'vehicles', [])
        },
        driversDiscrepancy: {
            data: _.get(discrepancyCategories, 'drivers', [])
        },
        priorInsuranceDiscrepancy: {
            data: _.get(discrepancyCategories, 'priorCarriers', [])
        },
        nextDescription: {
            content: (
                <FormattedMessage
                    {...messages.nextLabel}
                    values={{
                        yourDriverInfo: (
                            <strong>{translator(messages.yourDriverInfo)}</strong>
                        )
                    }}
                />
            )
        },
        ...generateOverrides()
    }

    const onNext = async () => {
        markAllDiscrepancyItemsCompleted(submissionVM);
        showProgressDialog(platformMessages.saveSubmission);
        const response = await LoadSaveService.resolveHVQDiscrepancies(submissionVM.value);
        hideProgressDialog();
        _.set(submissionVM, 'value', response);
        await processPaHVQConsumerAdditionalDriversCheck(submissionVM, currentStep.mlStepName, steps, changeNextSteps);

        goNext();
    }

    const resolvers = {
        resolveComponentMap: {
            mlHVQDiscrepancyPropertyGroupComponent: MLHVQDiscrepancyPropertyGroupComponent
        },
        resolveCallbackMap: {
            onNext
        }
    }

    return (
        <WizardPage
            showPrevious={false}
            showNext={false}
            showCancel={false}
            skipWhen={skipOnResumeIf}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

MLPAHVQConsumerDiscrepanciesPage.propTypes = wizardProps;

export default MLPAHVQConsumerDiscrepanciesPage;